import React, { useMemo } from "react";
import { Alert, Grid, Stack } from "@mui/material";
import ConfigHeader from "../ConfigHeader/config-header";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { ConfigBaseProps } from "../../../Pages/Basket/basket";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { FormExists, GetFormIsValid, GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { MailingListDto } from "../../../Core/Api/Types/get-mailing-list-response";
import {
  BasketShowOnlyAvailable,
  SettingRootField,
  UpdateItemSettingsRootOnBasket,
  UpdateItemStatusOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import { BasketItemStatus } from "../../../Core/Api/Types/basket-item-status";
import ConfigSaveButton from "../ConfigSaveButton/config-save-button";
import { GetOrderProductReadableType } from "../../../Utils/helpers";
import ConfigCommonSectionFormDelivery from "../ConfigCommon/config-common-section-form-delivery";
import FormSection from "../../Forms/FormSection/form-section";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import ConfigFormIcon from "../ConfigFormIcon";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ConfigCommonSectionFormScheduling from "../ConfigCommon/config-common-section-form-scheduling";
import { useTranslation } from "react-i18next";
interface ConfigSubtitleConverterProps extends ConfigBaseProps {
  storages: DbStorageInfos[];
  maillists: MailingListDto[];
}

const ConfigSubtitleConverter: React.FC<ConfigSubtitleConverterProps> = (props) => {
  const { t } = useTranslation();
  const { handleSubmit, handleCancel, handleChangeProduct, item, storages, maillists, ...otherProps } = props;
  const formId = `form-subconvert-${item.id}`;
  const formValid = useAppSelector(GetFormIsValid(formId));
  const formExists = useAppSelector(FormExists(formId));
  const dispatch = useAppDispatch();

  const toggleAvailableOnly = useAppSelector(BasketShowOnlyAvailable);

  // Item is updated automatically as it's passed as prop from parent and from redux orignally
  const tgop = item.selectedProduct?.settings;

  // Update settings
  const handleUpdateParam = (field: SettingRootField, value: any) => {
    dispatch(UpdateItemSettingsRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  const formValidTrigger = useMemo(() => {
    // In case of invalid, update item status
    if (!formValid && formExists && item.status !== BasketItemStatus.OptionsDone)
      dispatch(UpdateItemStatusOnBasket({ itemId: item.id, status: BasketItemStatus.New }));

    return formValid;
  }, [formValid]);

  // Form validator
  const formSectionFileSpecs = useAppSelector(GetSectionIsValid(formId, "sectionFileSpec"));

  const isSubtitleConfigValid = useMemo(() => {
    if (!tgop.fileFormat?.selectedValue || tgop.fileFormat?.selectedValue.id === 0) return true;

    let bad = tgop.warns?.filter((x) => x.outputFormat === tgop.fileFormat?.selectedValue.format).length > 0;

    if (bad) dispatch(UpdateItemStatusOnBasket({ itemId: item.id, status: BasketItemStatus.New }));

    return !bad;
  }, [tgop]);

  return (
    <Stack width={"100%"} {...otherProps} display={formExists ? "" : "none"}>
      <ConfigHeader
        configType={item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.SubtitleConverter}
        title={GetOrderProductReadableType(
          item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.TranscodeGeneric
        )}
        handleCancel={handleCancel}
        handleChangeProduct={handleChangeProduct}
        itemId={item.id}
      />
      <Stack spacing={1.5} width={"100%"} {...otherProps}>
        <FormSection
          icon={<ConfigFormIcon icon="settings" />}
          title={t("form:section.title.technicalSpecs")}
          status={formSectionFileSpecs && isSubtitleConfigValid ? "complete" : "warning"}
          statusText={formSectionFileSpecs ? t("common:configured") : t("common:configurationRequired")}
        >
          <Grid container spacing={3}>
            <Grid item md={7}>
              <FormAutocomplete
                label={t("field:label.fileFormat")}
                options={tgop.fileFormat?.values!}
                value={
                  tgop.fileFormat?.selectedValue && tgop.fileFormat?.selectedValue.id !== 0
                    ? tgop.fileFormat?.selectedValue
                    : null
                }
                multiple={false}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.FileFormat, v);
                }}
                getOptionLabel={(value: any) => (value && value.label ? value.label : "No option set")}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(x, s) => x.filter((a) => a.label?.toLowerCase().includes(s.inputValue.toLowerCase()))}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionFileSpec",
                  fieldId: "fileFormat",
                  required: true,
                  initialState: false,
                }}
              />

              {!isSubtitleConfigValid && (
                <Alert sx={{ mt: 1 }} color="warning" icon={<ErrorOutlineIcon />}>
                  {t("alert:warning.sourceFileIsNotCompatible", { format: tgop.fileFormat?.selectedValue.format })}
                </Alert>
              )}
            </Grid>

            <Grid item md={5}>
              <FormAutocomplete
                label={t("common:frameRate")}
                options={tgop.frameRate?.values!}
                value={tgop.frameRate?.selectedValue}
                multiple={false}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.Fps, v);
                }}
                getOptionLabel={(value: any) => (value.value === 0 ? "As Input" : value.value.toString())}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                filterOptions={(x, s) =>
                  x.filter((a) => a.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionFileSpec",
                  fieldId: "fps",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
          </Grid>
        </FormSection>
        <ConfigCommonSectionFormDelivery item={item} formid={formId} storages={storages} maillists={maillists} />
        <ConfigCommonSectionFormScheduling item={item} formid={formId} />
      </Stack>
      <ConfigSaveButton item={item} onSubmit={handleSubmit} disabled={!formValidTrigger || !isSubtitleConfigValid} />
    </Stack>
  );
};

export default ConfigSubtitleConverter;
